import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Download } from "lucide-react";
import { api } from "@/lib/api";
import { claimPurchase } from "@/lib/analytics";
import { VaultNextStep } from "@/components/VaultNextStep";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [downloads, setDownloads] = useState([]);
  const [product, setProduct] = useState("");
  const [courseUrl, setCourseUrl] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setProduct(data.lookup_key || "");
          claimPurchase(api, sessionId);
          try {
            const dl = await api.get(`/payments/downloads/${sessionId}`);
            setDownloads(dl.data.downloads || []);
            setCourseUrl(dl.data.coach_ready_url || null);
          } catch (e) { console.error("Download link fetch failed:", e); }
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch (e) { console.error("Payment status check failed:", e); }
      if (tries++ < 8) setTimeout(poll, 1800);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="payment-success-page">
      {status === "checking" && (
        <>
          <Loader2 className="mx-auto animate-spin text-[#2D2D2D]" size={40} />
          <p className="mt-4 text-[#6E6E6E]">Confirming your order…</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="mx-auto text-[#E8A33D]" size={48} />
          <h1 className="mt-4 font-head font-semibold uppercase text-3xl text-[#1E1E1E]">You're all set</h1>
          {courseUrl && (
            <div className="mt-5" data-testid="success-coach-ready">
              <p className="text-[#6E6E6E]">Your private Coach Ready link is below, and a copy is in your inbox.</p>
              <Link to={courseUrl} data-testid="success-start-coach-ready"
                className="mt-4 inline-flex items-center justify-center bg-[#1E1E1E] text-[#F5F0EA] font-head uppercase tracking-wide text-sm px-8 py-3.5 hover:bg-[#2D2D2D] transition-colors">
                Start Coach Ready
              </Link>
            </div>
          )}
          {downloads.length > 0 ? (
            <>
              <p className="mt-3 text-[#6E6E6E]">Your download is ready below and a copy is in your inbox. Links are good for 24 hours.</p>
              <div className="mt-6 space-y-3" data-testid="success-downloads">
                {downloads.map((d) => (
                  <a key={d.lookup_key} href={d.url} data-testid={`success-download-${d.lookup_key}`}
                    className="flex items-center justify-center gap-2 bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-6 py-3 hover:bg-[#D6922C] transition-colors">
                    <Download size={16} /> Download {d.name}
                  </a>
                ))}
              </div>
            </>
          ) : (
            !courseUrl && <p className="mt-3 text-[#6E6E6E]">Your receipt and download details are on their way by email. Thanks for the support — now go run a great practice.</p>
          )}
          {product === "coach_ready" && <VaultNextStep from="coach_ready_success" />}
          <Link to="/" className="mt-6 inline-block link-line text-[#2D2D2D]">Back home</Link>
        </>
      )}
      {["error", "timeout"].includes(status) && (
        <>
          <h1 className="font-head font-semibold uppercase text-3xl text-[#1E1E1E]">Hmm.</h1>
          <p className="mt-3 text-[#6E6E6E]">We couldn't confirm that payment yet. If you were charged, your download will still arrive by email.</p>
          <Link to="/products" className="mt-6 inline-block link-line text-[#2D2D2D]">Back to products</Link>
        </>
      )}
    </div>
  );
}
